import { TASTING_EXPERIENCE_STATUS, type TastingExperienceStatus } from '@wineryxplorer/shared';
import type { TastingExperienceFormErrors } from './types';
import { FormSection } from '../ui/FormSection';

export interface ExperienceVenueOption {
  id: string;
  name: string;
}

export interface ExperienceBasicInfoFieldsProps {
  showVenueSelect: boolean;
  venueOptions: ExperienceVenueOption[];
  venueId: string;
  onVenueIdChange: (value: string) => void;
  name: string;
  onNameChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  status: TastingExperienceStatus;
  onStatusChange: (value: TastingExperienceStatus) => void;
  errors: TastingExperienceFormErrors;
  touched: Record<string, boolean>;
  onBlur: (field: string) => void;
  isSubmitting: boolean;
}

export const ExperienceBasicInfoFields = ({
  showVenueSelect,
  venueOptions,
  venueId,
  onVenueIdChange,
  name,
  onNameChange,
  description,
  onDescriptionChange,
  status,
  onStatusChange,
  errors,
  touched,
  onBlur,
  isSubmitting,
}: ExperienceBasicInfoFieldsProps) => (
  <FormSection title="Basic Information">
    {/* Venue Selection (if not pre-selected) */}
    {showVenueSelect && (
      <div>
        <label htmlFor="venueId" className="block text-sm font-medium text-gray-700 mb-1">
          Venue <span className="text-red-500">*</span>
        </label>
        <select
          id="venueId"
          value={venueId}
          onChange={(e) => onVenueIdChange(e.target.value)}
          onBlur={() => onBlur('venueId')}
          disabled={isSubmitting}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
            touched.venueId && errors.venueId ? 'border-red-500' : 'border-gray-300'
          } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        >
          <option value="">Select a venue</option>
          {venueOptions.map((venue) => (
            <option key={venue.id} value={venue.id}>
              {venue.name}
            </option>
          ))}
        </select>
        {touched.venueId && errors.venueId && (
          <p className="mt-1 text-sm text-red-600">{errors.venueId}</p>
        )}
      </div>
    )}

    {/* Experience Name */}
    <div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
        Experience Name <span className="text-red-500">*</span>
      </label>
      <input
        id="name"
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onBlur={() => onBlur('name')}
        disabled={isSubmitting}
        placeholder="e.g., Premium Wine Tasting"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.name && errors.name ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        maxLength={200}
      />
      {touched.name && errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
    </div>

    {/* Description */}
    <div>
      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
        Description <span className="text-red-500">*</span>
      </label>
      <textarea
        id="description"
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        onBlur={() => onBlur('description')}
        disabled={isSubmitting}
        placeholder="Describe the tasting experience..."
        rows={4}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.description && errors.description ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.description && errors.description && (
        <p className="mt-1 text-sm text-red-600">{errors.description}</p>
      )}
    </div>

    {/* Status */}
    <div>
      <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
        Status <span className="text-red-500">*</span>
      </label>
      <select
        id="status"
        value={status}
        onChange={(e) => onStatusChange(e.target.value as TastingExperienceStatus)}
        disabled={isSubmitting}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        {TASTING_EXPERIENCE_STATUS.map((statusOption) => (
          <option key={statusOption} value={statusOption}>
            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
          </option>
        ))}
      </select>
      <p className="mt-1 text-sm text-gray-500">
        Draft experiences are only visible to venue admins. Published experiences are visible to
        everyone.
      </p>
    </div>
  </FormSection>
);
